import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar, Toolbar, IconButton, Typography, Button, Box, Avatar,
  Menu, MenuItem, Drawer, List, ListItemButton, ListItemIcon, ListItemText,
  Badge, Divider, Chip, Tooltip
} from '@mui/material';
import {
  Menu as MenuIcon, Close as CloseIcon,
  LogoutOutlined, DashboardOutlined,
  BarChartOutlined, LocalHospitalOutlined, ChatOutlined,
  WbSunnyOutlined, PersonOutlined, AdminPanelSettingsOutlined,
  KeyboardArrowDown, BusinessOutlined, SpaOutlined
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getInitials } from '../utils/helpers';
import NotificationBell from './NotificationBell';
import LanguageDropdown from './LanguageDropdown';
import AppSwitcher from './AppSwitcher';

const PRIMARY = '#2E7D32';
const PRIMARY_DARK = '#1B5E20';

const Navbar = ({ onMenuClick }) => {
  const { currentUser, userProfile, logout, userRole, hasFarmerProfile, hasCompanyProfile } = useAuth();

  // Portal routing
  const seedMapPath = hasCompanyProfile ? '/business' : '/seeds';
  const { language, t } = useLanguage();

  const NAV_LINKS = [
    { label: t.nav.dashboard, path: '/dashboard', icon: <DashboardOutlined fontSize="small" /> },
    { label: t.nav.prices, path: '/prices', icon: <BarChartOutlined fontSize="small" /> },
    { label: t.nav.cropDoctor, path: '/crop-doctor', icon: <LocalHospitalOutlined fontSize="small" /> },
    { label: t.nav.aiChat, path: '/ai-chat', icon: <ChatOutlined fontSize="small" /> },
    { label: t.nav.weather, path: '/weather', icon: <WbSunnyOutlined fontSize="small" /> },
  ];
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
    setAnchorEl(null);
  };

  const isActive = (path) => location.pathname === path;
  const isLanding = location.pathname === '/';
  const isDashboard = currentUser && !isLanding;

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: isLanding && !scrolled
            ? 'transparent'
            : scrolled
              ? 'rgba(255,255,255,0.98)'
              : '#ffffff',
          backdropFilter: scrolled ? 'blur(16px)' : 'none',
          borderBottom: isDashboard
            ? '1px solid rgba(46,125,50,0.1)'
            : scrolled ? '1px solid rgba(0,0,0,0.06)' : 'none',
          boxShadow: scrolled ? '0 2px 16px rgba(0,0,0,0.06)' : 'none',
          transition: 'all 0.3s ease',
          zIndex: 1300,
        }}
      >
        <Toolbar
          sx={{
            maxWidth: '100%',
            width: '100%',
            px: { xs: 2, md: 3 },
            minHeight: '64px !important',
            gap: 1,
          }}
        >
          {/* Hamburger for sidebar toggle (dashboard) */}
          {currentUser && (
            <Tooltip title="Toggle sidebar" arrow>
              <IconButton
                onClick={onMenuClick}
                size="small"
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  mr: 0.5,
                  color: '#546E7A',
                  bgcolor: 'transparent',
                  '&:hover': { bgcolor: 'rgba(46,125,50,0.08)', color: PRIMARY },
                  borderRadius: '10px',
                  width: 36,
                  height: 36,
                }}
              >
                <MenuIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {/* Logo */}
          <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Box
              component="img"
              src="/logo192.png"
              alt="FassalkartAI"
              sx={{
                width: 36,
                height: 36,
                flexShrink: 0,
                objectFit: 'contain',
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                fontSize: '1.2rem',
                color: isLanding && !scrolled ? 'white' : PRIMARY,
                fontFamily: 'Poppins, sans-serif',
                letterSpacing: '-0.3px',
                lineHeight: 1,
              }}
            >
              Fassalkart<Box component="span" sx={{ color: isLanding && !scrolled ? 'rgba(255,255,255,0.85)' : '#FF8F00' }}>AI</Box>
            </Typography>
          </Link>

          <Box sx={{ flex: 1 }} />

          {/* Desktop Nav Links */}
          {currentUser && (
            <Box sx={{ display: { xs: 'none', lg: 'flex' }, gap: 0.5, mr: 1 }}>
              {NAV_LINKS.map(link => (
                <Button
                  key={link.path}
                  component={Link}
                  to={link.path}
                  startIcon={link.icon}
                  sx={{
                    color: isActive(link.path)
                      ? PRIMARY
                      : (isLanding && !scrolled ? 'rgba(255,255,255,0.88)' : '#546E7A'),
                    fontWeight: isActive(link.path) ? 700 : 500,
                    fontSize: '12.5px',
                    px: 1.5,
                    py: 0.75,
                    borderRadius: '10px',
                    background: isActive(link.path)
                      ? 'rgba(46,125,50,0.08)'
                      : 'transparent',
                    fontFamily: 'Poppins, sans-serif',
                    textTransform: 'none',
                    minWidth: 0,
                    '&:hover': {
                      background: isActive(link.path)
                        ? 'rgba(46,125,50,0.12)'
                        : 'rgba(0,0,0,0.04)',
                      color: PRIMARY,
                    },
                    '& .MuiButton-startIcon': { mr: 0.5 },
                  }}
                >
                  {link.label}
                </Button>
              ))}
            </Box>
          )}

          {/* App Switcher pill — desktop */}
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, mr: 0.5 }}>
            <AppSwitcher currentApp="fassalkart" variant={isLanding && !scrolled ? 'dark' : 'light'} />
          </Box>

          {/* Right Controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* Language Dropdown */}
            <LanguageDropdown compact={false} dark={isLanding && !scrolled} />

            {currentUser ? (
              <>
                {/* Notification Bell */}
                <NotificationBell />

                {/* User Avatar Button (Desktop) */}
                <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                  <Button
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    endIcon={<KeyboardArrowDown fontSize="small" sx={{ opacity: 0.6 }} />}
                    sx={{
                      p: '4px 8px',
                      borderRadius: '12px',
                      color: isLanding && !scrolled ? 'white' : '#424242',
                      textTransform: 'none',
                      gap: 0.75,
                      '&:hover': { bgcolor: 'rgba(46,125,50,0.06)' },
                      minWidth: 0,
                      border: '1px solid',
                      borderColor: isLanding && !scrolled
                        ? 'rgba(255,255,255,0.25)'
                        : 'rgba(0,0,0,0.08)',
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 30,
                        height: 30,
                        background: `linear-gradient(135deg, ${PRIMARY} 0%, #43A047 100%)`,
                        fontSize: '12px',
                        fontWeight: 700,
                      }}
                    >
                      {getInitials(userProfile?.name || currentUser?.displayName || 'K')}
                    </Avatar>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        maxWidth: 80,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontFamily: 'Poppins, sans-serif',
                        display: { xs: 'none', lg: 'block' },
                      }}
                    >
                      {userProfile?.name?.split(' ')[0] || 'Kisan'}
                    </Typography>
                  </Button>
                </Box>

                {/* Hamburger (Mobile) */}
                <IconButton
                  sx={{ display: { md: 'none' }, color: isLanding && !scrolled ? 'white' : '#546E7A' }}
                  onClick={() => setDrawerOpen(true)}
                >
                  <MenuIcon />
                </IconButton>
              </>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  component={Link}
                  to="/business/register"
                  variant="outlined"
                  size="small"
                  startIcon={<BusinessOutlined fontSize="small" />}
                  sx={{
                    display: { xs: 'none', sm: 'flex' },
                    borderColor: '#F5A623',
                    color: isLanding && !scrolled ? '#F5A623' : '#E09510',
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: 12,
                    fontFamily: 'Poppins, sans-serif',
                    '&:hover': {
                      bgcolor: '#FFF3E0',
                      borderColor: '#E09510',
                    },
                  }}
                >
                  For Seed Companies
                </Button>
                <Button
                  component={Link}
                  to="/login"
                  variant="outlined"
                  size="small"
                  sx={{
                    borderColor: isLanding && !scrolled ? 'rgba(255,255,255,0.5)' : PRIMARY,
                    color: isLanding && !scrolled ? 'white' : PRIMARY,
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: 13,
                    fontFamily: 'Poppins, sans-serif',
                    '&:hover': {
                      borderColor: isLanding && !scrolled ? 'white' : PRIMARY_DARK,
                      background: isLanding && !scrolled ? 'rgba(255,255,255,0.1)' : 'rgba(46,125,50,0.05)',
                    },
                  }}
                >
                  {t.nav.login}
                </Button>
                <Button
                  component={Link}
                  to="/register"
                  variant="contained"
                  size="small"
                  sx={{
                    background: `linear-gradient(135deg, ${PRIMARY} 0%, #43A047 100%)`,
                    color: 'white',
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: 13,
                    fontFamily: 'Poppins, sans-serif',
                    boxShadow: '0 2px 8px rgba(46,125,50,0.35)',
                    '&:hover': {
                      background: `linear-gradient(135deg, ${PRIMARY_DARK} 0%, ${PRIMARY} 100%)`,
                      boxShadow: '0 4px 12px rgba(46,125,50,0.45)',
                    },
                  }}
                >
                  {t.nav.register}
                </Button>
              </Box>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* User Menu Dropdown */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: '14px',
            minWidth: 210,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            border: '1px solid rgba(0,0,0,0.06)',
            overflow: 'hidden',
          }
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            background: 'linear-gradient(135deg, #E8F5E9 0%, #F1F8E9 100%)',
            borderBottom: '1px solid rgba(46,125,50,0.1)',
          }}
        >
          <Typography fontWeight={700} fontSize={14} fontFamily="Poppins, sans-serif" color={PRIMARY}>
            {userProfile?.name || 'Kisan'}
          </Typography>
          <Typography color="text.secondary" fontSize={12} fontFamily="Poppins, sans-serif">
            {userProfile?.district}{userProfile?.state ? `, ${userProfile.state}` : ''}
          </Typography>
        </Box>
        {/* FassalkartAI portal link — only for dual-portal users already on seedmap side */}
        {hasCompanyProfile && hasFarmerProfile && (
          <MenuItem
            onClick={() => { navigate('/dashboard'); setAnchorEl(null); }}
            sx={{ py: 1.2, '&:hover': { bgcolor: 'rgba(46,125,50,0.08)' } }}
          >
            <Box component="img" src="/logo192.png" sx={{ mr: 1.5, width: 18, height: 18, objectFit: 'contain' }} />
            <Typography fontSize={13} fontFamily="Poppins, sans-serif" fontWeight={600} color="#2E7D32">
              FassalkartAI
            </Typography>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => { navigate(seedMapPath); setAnchorEl(null); }}
          sx={{ py: 1.2, '&:hover': { bgcolor: 'rgba(245,166,35,0.08)' } }}
        >
          <SpaOutlined sx={{ mr: 1.5, fontSize: 18, color: '#E09510' }} />
          <Typography fontSize={13} fontFamily="Poppins, sans-serif" fontWeight={600} color="#E09510">
            SeedMap AI
          </Typography>
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem
          onClick={() => { navigate('/profile'); setAnchorEl(null); }}
          sx={{ py: 1.2, '&:hover': { bgcolor: 'rgba(46,125,50,0.06)' } }}
        >
          <PersonOutlined sx={{ mr: 1.5, fontSize: 18, color: PRIMARY }} />
          <Typography fontSize={13} fontFamily="Poppins, sans-serif" fontWeight={500}>
            {t.nav.profile}
          </Typography>
        </MenuItem>
        {userProfile?.role === 'admin' && (
          <MenuItem
            onClick={() => { navigate('/admin'); setAnchorEl(null); }}
            sx={{ py: 1.2, '&:hover': { bgcolor: 'rgba(255,143,0,0.06)' } }}
          >
            <AdminPanelSettingsOutlined sx={{ mr: 1.5, fontSize: 18, color: '#FF8F00' }} />
            <Typography fontSize={13} fontFamily="Poppins, sans-serif" fontWeight={500}>Admin Panel</Typography>
          </MenuItem>
        )}
        <Divider sx={{ my: 0.5 }} />
        <MenuItem
          onClick={handleLogout}
          sx={{ py: 1.2, color: '#D32F2F', '&:hover': { bgcolor: 'rgba(211,47,47,0.06)' } }}
        >
          <LogoutOutlined sx={{ mr: 1.5, fontSize: 18 }} />
          <Typography fontSize={13} fontFamily="Poppins, sans-serif" fontWeight={500}>
            {t.nav.logout}
          </Typography>
        </MenuItem>
      </Menu>


      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 280,
            background: '#FAFFFE',
            borderTopLeftRadius: 16,
            borderBottomLeftRadius: 16,
          }
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box component="img" src="/logo192.png" alt="FassalkartAI" sx={{ width: 28, height: 28, objectFit: 'contain' }} />
            <Typography fontWeight={800} color={PRIMARY} fontSize={17} fontFamily="Poppins, sans-serif">
              Fassalkart<Box component="span" sx={{ color: '#FF8F00' }}>AI</Box>
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {currentUser && <AppSwitcher currentApp="fassalkart" />}
            <IconButton
              onClick={() => setDrawerOpen(false)}
              sx={{ color: '#546E7A', '&:hover': { bgcolor: 'rgba(46,125,50,0.08)', color: PRIMARY } }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {currentUser && (
          <Box
            sx={{
              px: 2,
              py: 1.5,
              mb: 1,
              background: 'linear-gradient(135deg, #E8F5E9 0%, #F1F8E9 100%)',
              borderRadius: '12px',
              mx: 2,
              border: '1px solid rgba(46,125,50,0.12)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  background: `linear-gradient(135deg, ${PRIMARY} 0%, #43A047 100%)`,
                  fontWeight: 700,
                  fontSize: 14,
                }}
              >
                {getInitials(userProfile?.name || 'K')}
              </Avatar>
              <Box>
                <Typography fontWeight={700} fontSize={14} fontFamily="Poppins, sans-serif">
                  {userProfile?.name || 'किसान'}
                </Typography>
                <Typography color="text.secondary" fontSize={12} fontFamily="Poppins, sans-serif">
                  {userProfile?.district || '—'}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        <Divider sx={{ my: 1 }} />

        <List sx={{ px: 1 }}>
          {NAV_LINKS.map(link => (
            <ListItemButton
              key={link.path}
              onClick={() => { navigate(link.path); setDrawerOpen(false); }}
              selected={isActive(link.path)}
              sx={{
                mx: 0.5,
                borderRadius: '10px',
                mb: 0.5,
                '&.Mui-selected': {
                  background: 'linear-gradient(135deg, rgba(46,125,50,0.12) 0%, rgba(76,175,80,0.06) 100%)',
                  color: PRIMARY,
                  borderLeft: `3px solid ${PRIMARY}`,
                },
                '&.Mui-selected .MuiListItemIcon-root': { color: PRIMARY },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>{link.icon}</ListItemIcon>
              <ListItemText
                primary={link.label}
                primaryTypographyProps={{ fontSize: 13, fontWeight: 500, fontFamily: 'Poppins, sans-serif' }}
              />
            </ListItemButton>
          ))}

          {/* FassalkartAI — shown for dual-portal users */}
          {currentUser && hasCompanyProfile && hasFarmerProfile && (
            <ListItemButton
              onClick={() => { navigate('/dashboard'); setDrawerOpen(false); }}
              selected={!location.pathname.startsWith('/business')}
              sx={{
                mx: 0.5, borderRadius: '10px', mb: 0.5,
                background: 'linear-gradient(135deg, #E8F5E9 0%, #F1F8E9 100%)',
                border: '1px solid rgba(46,125,50,0.25)',
                '&:hover': { background: 'linear-gradient(135deg, #C8E6C9 0%, #E8F5E9 100%)' },
                '&.Mui-selected': { background: 'linear-gradient(135deg, #A5D6A7 0%, #C8E6C9 100%)', borderLeft: '3px solid #2E7D32' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Box component="img" src="/logo192.png" sx={{ width: 20, height: 20, objectFit: 'contain' }} />
              </ListItemIcon>
              <ListItemText
                primary="FassalkartAI"
                secondary="Farmer Portal"
                primaryTypographyProps={{ fontSize: 13, fontWeight: 700, fontFamily: 'Poppins, sans-serif', color: '#2E7D32' }}
                secondaryTypographyProps={{ fontSize: 11, fontFamily: 'Poppins, sans-serif' }}
              />
            </ListItemButton>
          )}

          {/* SeedMap AI — always visible in mobile drawer */}
          <ListItemButton
            onClick={() => {
              navigate(currentUser ? seedMapPath : '/seeds');
              setDrawerOpen(false);
            }}
            selected={location.pathname.startsWith('/seeds') || location.pathname.startsWith('/business')}
            sx={{
              mx: 0.5,
              borderRadius: '10px',
              mb: 0.5,
              background: 'linear-gradient(135deg, #FFF8E1 0%, #FFF3CD 100%)',
              border: '1px solid rgba(245,166,35,0.3)',
              '&:hover': { background: 'linear-gradient(135deg, #FFF3CD 0%, #FFE082 100%)' },
              '&.Mui-selected': {
                background: 'linear-gradient(135deg, #FFE082 0%, #FFD740 100%)',
                borderLeft: '3px solid #F5A623',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <SpaOutlined sx={{ fontSize: 20, color: '#E09510' }} />
            </ListItemIcon>
            <ListItemText
              primary="SeedMap AI"
              secondary={hasCompanyProfile ? 'Business Dashboard' : 'Find Best Seeds'}
              primaryTypographyProps={{ fontSize: 13, fontWeight: 700, fontFamily: 'Poppins, sans-serif', color: '#E09510' }}
              secondaryTypographyProps={{ fontSize: 11, fontFamily: 'Poppins, sans-serif' }}
            />
          </ListItemButton>

          {/* For logged-out users: seed company register link */}
          {!currentUser && (
            <ListItemButton
              onClick={() => { navigate('/business/register'); setDrawerOpen(false); }}
              sx={{ mx: 0.5, borderRadius: '10px', mb: 0.5 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <BusinessOutlined sx={{ fontSize: 20, color: '#F5A623' }} />
              </ListItemIcon>
              <ListItemText
                primary="For Seed Companies"
                secondary="Register your business"
                primaryTypographyProps={{ fontSize: 13, fontWeight: 600, fontFamily: 'Poppins, sans-serif' }}
                secondaryTypographyProps={{ fontSize: 11, fontFamily: 'Poppins, sans-serif' }}
              />
            </ListItemButton>
          )}
        </List>

        <Divider sx={{ my: 1 }} />

        <List sx={{ px: 1 }}>
          <ListItemButton
            onClick={() => { navigate('/profile'); setDrawerOpen(false); }}
            sx={{ borderRadius: '10px', mx: 0.5 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}><PersonOutlined sx={{ fontSize: 20 }} /></ListItemIcon>
            <ListItemText
              primary={t.nav.profile}
              primaryTypographyProps={{ fontSize: 13, fontWeight: 500, fontFamily: 'Poppins, sans-serif' }}
            />
          </ListItemButton>

          {userProfile?.role === 'admin' && (
            <ListItemButton
              onClick={() => { navigate('/admin'); setDrawerOpen(false); }}
              sx={{ borderRadius: '10px', mx: 0.5 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <AdminPanelSettingsOutlined sx={{ fontSize: 20, color: '#FF8F00' }} />
              </ListItemIcon>
              <ListItemText
                primary="Admin Panel"
                primaryTypographyProps={{ fontSize: 13, fontWeight: 500, fontFamily: 'Poppins, sans-serif' }}
              />
            </ListItemButton>
          )}

          <ListItemButton
            onClick={handleLogout}
            sx={{ borderRadius: '10px', mx: 0.5, color: '#D32F2F', '&:hover': { bgcolor: 'rgba(211,47,47,0.06)' } }}
          >
            <ListItemIcon sx={{ minWidth: 36, color: '#D32F2F' }}>
              <LogoutOutlined sx={{ fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText
              primary={t.nav.logout}
              primaryTypographyProps={{ fontSize: 13, fontWeight: 500, fontFamily: 'Poppins, sans-serif' }}
            />
          </ListItemButton>
        </List>
      </Drawer>
    </>
  );
};

export default Navbar;
